import { z } from 'zod';

import { defineService } from '@@api/lib/defineService';
import {
    Bookmark,
    UnityCategoryPage,
    CategorySection,
    ContentBlockSection,
    CopySectionParams,
    CopySectionWarning,
    CustomSection,
    DefaultCategorySection,
    DefaultTagSection,
    NoLogicSection,
    Preset,
    ExternalWidgetSection,
    Section,
    SectionContent,
    SectionLayout,
    TaPlacementEngineSection,
    UnityTagPage,
    TagSection,
    UnitySection,
    UserCategorySelectionSection,
} from '@@api/services/placement/schemas';
import {
    deserializeCategoryPage,
    deserializeSection,
    deserializeTagPage,
    serializeSection,
    serializeSections,
} from '@@api/services/placement/transformers';
import { PaginationParams } from '@@api/utils/schemas/queryParams';
import { Ids } from '@@api/utils/schemas/schemas';
import { paginated } from '@@api/utils/schemas/utils';
import config from '@@config';

export const placementService = defineService(
    (defineEndpoint) => ({
        bookmark: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/bookmarks',
                query: z.object({
                    ...PaginationParams.shape,
                    metadataIds: Ids.optional(),
                }),
                responses: {
                    200: paginated(Bookmark),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/bookmarks',
                body: Bookmark,
                responses: {
                    201: Bookmark,
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/bookmarks/:id',
                pathParams: z.object({
                    id: z.number(),
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
        categoryPage: {
            get: defineEndpoint({
                method: 'GET',
                path: '/categoryPage/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                responses: {
                    200: UnityCategoryPage.transform(deserializeCategoryPage),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/categoryPage/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: z
                    .array(
                        z.union([
                            DefaultCategorySection,
                            CategorySection.partial({ id: true }),
                            CustomSection.partial({ id: true }),
                            TagSection.partial({ id: true }),
                            UserCategorySelectionSection.partial({ id: true }),
                            NoLogicSection.partial({ id: true }),
                            TaPlacementEngineSection.partial({ id: true }),
                            ExternalWidgetSection.partial({ id: true }),
                            ContentBlockSection.partial({ id: true }),
                        ]),
                    )
                    .transform(serializeSections),
                responses: {
                    200: UnityCategoryPage.transform(deserializeCategoryPage),
                },
            }),
            patch: defineEndpoint({
                method: 'PATCH',
                path: '/categoryPage/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: z.object({
                    allowAbTest: z.boolean(),
                }),
                responses: {
                    200: UnityCategoryPage.transform(deserializeCategoryPage),
                },
            }),
            publish: defineEndpoint({
                method: 'POST',
                path: '/categoryPage/:id/publish',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: z.undefined(),
                responses: {
                    200: z.undefined(),
                },
            }),
        },
        tagPage: {
            get: defineEndpoint({
                method: 'GET',
                path: '/tagPage/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                responses: {
                    200: UnityTagPage.transform(deserializeTagPage),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/tagPage/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: z
                    .array(z.union([DefaultTagSection, ContentBlockSection.partial({ id: true })]))
                    .transform(serializeSections),
                responses: {
                    200: UnityTagPage.transform(deserializeTagPage),
                },
            }),
            publish: defineEndpoint({
                method: 'POST',
                path: '/tagPage/:id/publish',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: z.undefined(),
                responses: {
                    200: z.undefined(),
                },
            }),
        },
        section: {
            get: defineEndpoint({
                method: 'GET',
                path: '/sections/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                query: z.object({
                    includePreviewUrl: z.literal(true).optional(),
                }),
                responses: {
                    200: UnitySection.transform(deserializeSection),
                },
            }),
            put: defineEndpoint({
                method: 'PUT',
                path: '/sections/:id',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: Section.transform(serializeSection),
                responses: {
                    200: UnitySection.transform(deserializeSection),
                },
            }),
            getContent: defineEndpoint({
                method: 'GET',
                path: '/sections/:id/content',
                pathParams: z.object({
                    id: z.string(),
                }),
                query: z.object({
                    includePreviewUrl: z.literal(true).optional(),
                }),
                responses: {
                    200: SectionContent,
                },
            }),
            putContent: defineEndpoint({
                method: 'PUT',
                path: '/sections/:id/content',
                pathParams: z.object({
                    id: z.string(),
                }),
                body: SectionContent,
                responses: {
                    200: SectionContent,
                },
            }),
            getHistory: defineEndpoint({
                method: 'GET',
                path: '/sections/:id/history',
                pathParams: z.object({
                    id: z.string(),
                }),
                query: z.object({
                    ...PaginationParams.pick({ size: true }).shape,
                }),
                responses: {
                    200: paginated(SectionContent),
                },
            }),
            layouts: defineEndpoint({
                method: 'GET',
                path: '/sections/layouts',
                responses: {
                    200: z.array(SectionLayout),
                },
            }),
            copy: defineEndpoint({
                method: 'POST',
                path: '/sections',
                body: z.undefined(),
                query: CopySectionParams,
                responses: {
                    201: z.array(CopySectionWarning),
                },
            }),
        },
        preset: {
            getAll: defineEndpoint({
                method: 'GET',
                path: '/presets',
                query: z.object({
                    ...PaginationParams.pick({ size: true }).shape,
                }),
                responses: {
                    200: paginated(Preset),
                },
            }),
            post: defineEndpoint({
                method: 'POST',
                path: '/presets',
                body: Preset.partial({ id: true }),
                responses: {
                    201: Preset,
                },
            }),
            delete: defineEndpoint({
                method: 'DELETE',
                path: '/presets/:id',
                pathParams: z.object({
                    id: Preset.shape.id,
                }),
                responses: {
                    204: z.never(),
                },
            }),
        },
    }),
    {
        baseUrl: config.apiEnvVars.placementUrl,
    },
);
